import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { CalendarDays, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICES } from "@/lib/site";
import { BookingSuccess } from "./BookingSuccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "h-12 rounded-none border-white/20 bg-black text-white placeholder:text-neutral-500 focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand";
const today = new Date(new Date().setHours(0, 0, 0, 0));

const VALID = SERVICES.map((s) => s.key);

export const BookingForm = ({ defaultService }) => {
  const initial = { name: "", phone: "", service: VALID.includes(defaultService) ? defaultService : "", people: "1", note: "" };
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service || !date) return toast.error("Lütfen hizmet ve tarih seçin.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, people: Number(form.people) || 1, date: format(date, "yyyy-MM-dd") });
      setDone(data);
      toast.success("Rezervasyon talebiniz alındı!");
    } catch (err) {
      toast.error("Gönderilemedi. Lütfen bilgileri kontrol edip tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return <BookingSuccess booking={done} onReset={() => { setDone(null); setDate(undefined); setForm(initial); }} />;

  return (
    <form onSubmit={submit} data-testid="booking-form" className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] text-neutral-400">Ad Soyad</Label>
        <Input id="name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Adınız" className={field} data-testid="booking-name-input" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] text-neutral-400">Telefon</Label>
        <Input id="phone" type="tel" required minLength={7} value={form.phone} onChange={set("phone")} placeholder="05xx xxx xx xx" className={field} data-testid="booking-phone-input" />
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Hizmet</Label>
        <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
          <SelectTrigger className={field} data-testid="booking-service-select"><SelectValue placeholder="Hizmet seçin" /></SelectTrigger>
          <SelectContent className="rounded-none border-white/20 bg-neutral-950 text-white">
            {SERVICES.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`booking-service-option-${s.key}`} className="rounded-none focus:bg-brand focus:text-white">{s.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Tarih</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid="booking-date-trigger" className={`${field} flex w-full items-center justify-between border px-3 text-left text-sm`}>
              <span className={date ? "text-white" : "text-neutral-500"}>{date ? format(date, "d MMMM yyyy, EEEE", { locale: tr }) : "Tarih seçin"}</span>
              <CalendarDays className="h-4 w-4 text-neutral-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto rounded-none border-white/20 bg-neutral-950 p-0 text-white" data-testid="booking-calendar">
            <Calendar mode="single" locale={tr} selected={date} onSelect={(d) => { setDate(d); setOpen(false); }} disabled={{ before: today }} initialFocus />
          </PopoverContent>
        </Popover>
      </div>
      <div className="space-y-2">
        <Label htmlFor="people" className="text-xs uppercase tracking-[0.2em] text-neutral-400">Kişi sayısı</Label>
        <Input id="people" type="number" min={1} max={20} value={form.people} onChange={set("people")} className={field} data-testid="booking-people-input" />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="note" className="text-xs uppercase tracking-[0.2em] text-neutral-400">Not (isteğe bağlı)</Label>
        <Textarea id="note" maxLength={500} value={form.note} onChange={set("note")} placeholder="Deneyim seviyeniz, çocuğun yaşı, saat tercihi..." className={`${field} min-h-[100px]`} data-testid="booking-note-input" />
      </div>
      <button type="submit" disabled={loading} data-testid="booking-submit-btn" className="group flex items-center justify-center gap-3 bg-brand px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-brand disabled:opacity-60 sm:col-span-2">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Rezervasyon talebi gönder <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></>}
      </button>
    </form>
  );
};
